import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../sessionSlice";
import { apiUrl } from "../../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../../utils/themeAssets";
import "./yogaUploads.css";

const PAGE_SIZE = 10;

function matchesSearch(row, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const name = String(row.name || "").toLowerCase();
  const email = String(row.email || "").toLowerCase();
  const status = row.visibleInGallery ? "visible" : "hidden";
  const points = String(row.points ?? "");
  const likes = String(row.likeCount ?? "");
  const time = String(row.time || "").toLowerCase();

  return (
    name.includes(q) ||
    email.includes(q) ||
    status.includes(q) ||
    points.includes(q) ||
    likes.includes(q) ||
    time.includes(q)
  );
}

export default function YogaUploads() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const loadUploads = useCallback(async () => {
    if (!adminToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(apiUrl("/adminYogaUploads"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({}),
      });
      const payload = await resp.json();
      if (resp.ok && payload?.success) {
        setUploads(Array.isArray(payload.uploads) ? payload.uploads : []);
      } else {
        setUploads([]);
      }
    } catch (e) {
      console.error("adminYogaUploads", e);
      setUploads([]);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filteredUploads = useMemo(
    () => uploads.filter((row) => matchesSearch(row, search)),
    [uploads, search]
  );

  const totalPages = Math.max(1, Math.ceil(filteredUploads.length / PAGE_SIZE));

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredUploads.slice(start, start + PAGE_SIZE);
  }, [filteredUploads, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const downloadCsv = () => {
    const headers = ["Name", "Email", "Time", "Points", "Likes", "Gallery", "Files"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredUploads.map((r) =>
      [
        r.name,
        r.email,
        r.time,
        r.points,
        r.likeCount,
        r.visibleInGallery ? "Visible" : "Hidden",
        (r.files || []).map((f) => s3ThemeAssetUrl(f.filename)).join(" | "),
      ]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [headers.map(esc).join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `uploads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleVisibility = async (row) => {
    if (!adminToken || togglingId) return;
    const nextStatus = row.visibleInGallery ? 0 : 1;
    setTogglingId(row.id);
    try {
      const resp = await fetch(apiUrl("/adminYogaUploadVisibility"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ gameId: row.id, uploadStatus: nextStatus }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        throw new Error(payload?.error || "Update failed");
      }
      setUploads((prev) =>
        prev.map((u) =>
          u.id === row.id
            ? { ...u, uploadStatus: nextStatus, visibleInGallery: nextStatus === 1 }
            : u
        )
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Could not update visibility.", "error");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="yau-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back to rules
        </button>
      </div>

      <h1 className="yau-title">Gallery uploads</h1>
      <p className="yau-sub">
        Hide uploads from the public gallery without deleting files. Visible uploads appear on the
        player gallery page.
      </p>

      {!loading && uploads.length > 0 ? (
        <div className="yau-toolbar">
          <input
            type="search"
            className="yau-search"
            placeholder="Search by name, email, visibility, points, likes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search uploads"
          />
          <span className="yau-count">
            {filteredUploads.length} of {uploads.length} upload{uploads.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            className="yau-download-btn"
            onClick={downloadCsv}
            disabled={filteredUploads.length === 0}
          >
            <i className="fa-solid fa-download" /> Download CSV
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="load-theme-container">
          <div className="load-theme-spinner" />
        </div>
      ) : uploads.length === 0 ? (
        <p className="yau-empty">No player uploads yet for this session.</p>
      ) : filteredUploads.length === 0 ? (
        <p className="yau-empty">No uploads match your search.</p>
      ) : (
        <>
          <div className="yau-table-wrap">
            <table className="yau-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Files</th>
                  <th>Points</th>
                  <th>Likes</th>
                  <th>Gallery</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((row) => (
                  <tr key={row.id} className={row.visibleInGallery ? "" : "yau-row--hidden"}>
                    <td>
                      <div className="yau-player">{row.name || row.email || "—"}</div>
                      <div className="yau-meta">{row.time || ""}</div>
                    </td>
                    <td>
                      <div className="yau-files">
                        {(row.files || []).map((f) => (
                          <a
                            key={f.filename}
                            href={s3ThemeAssetUrl(f.filename)}
                            target="_blank"
                            rel="noreferrer"
                            className="yau-file-link"
                          >
                            {f.mediaType === "video" ? "▶ Video" : "🖼 Image"}
                          </a>
                        ))}
                      </div>
                    </td>
                    <td>{row.points}</td>
                    <td>{row.likeCount}</td>
                    <td>
                      <span
                        className={`yau-badge${row.visibleInGallery ? " yau-badge--on" : " yau-badge--off"}`}
                      >
                        {row.visibleInGallery ? "Visible" : "Hidden"}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="yau-toggle-btn"
                        disabled={togglingId === row.id}
                        onClick={() => toggleVisibility(row)}
                      >
                        {row.visibleInGallery ? "Hide" : "Show"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <nav className="yau-pagination" aria-label="Upload pages">
              <button
                type="button"
                className="yau-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="yau-page-info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="yau-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}
    </div>
  );
}
